import useLocalStorage from 'hooks/useLocalStorage';

import React, { useState } from 'react';

import { FormValues as PostFormValues } from 'components/PostForm/PostForm';

export type DraftPost = PostFormValues & {
  draftId: string;
};

export const GlobalStateContext = React.createContext<{
  expandedContentIds: string[];
  expandCollapseContent: (id: string) => void;
  isContentExpanded: (id: string) => boolean;
  isPostToolbarVisible: boolean;
  setIsPostToolbarVisible: (isVisible: boolean) => void;
  draftPosts: DraftPost[];
  createOrUpdateDraftPost: (post: DraftPost) => DraftPost | undefined;
  getDraftPost: (id: string) => DraftPost | undefined;
  removeDraftPost: (id: string) => void;
  lastConversationId: string | null;
  setLastConversationId: (id: string | null) => void;
}>({
  expandedContentIds: [],
  expandCollapseContent: () => {},
  isContentExpanded: () => false,
  isPostToolbarVisible: false,
  setIsPostToolbarVisible: () => {},
  draftPosts: [],
  createOrUpdateDraftPost: () => undefined,
  getDraftPost: () => undefined,
  removeDraftPost: () => {},
  lastConversationId: null,
  setLastConversationId: () => {},
});

export const useGlobalState = () => React.useContext(GlobalStateContext);

export const GlobalStateProvider = ({ children }: { children: React.ReactNode }) => {
  const { saveKeyInLocalStorage, getKeyFromLocalStorage, isSaved } = useLocalStorage('draftPosts');

  const [lastConversationId, setLastConversationId] = useState<string | null>(null);
  const [isPostToolbarVisible, setIsPostToolbarVisible] = useState(false);
  const [expandedContentIds, setExpandedContentIds] = useState<string[]>([]);
  const [draftPosts, setDraftPosts] = useState<DraftPost[]>(
    isSaved ? ((getKeyFromLocalStorage() || []) as DraftPost[]) : []
  );

  const expandCollapseContent = (id: string) => {
    if (expandedContentIds.includes(id)) {
      setExpandedContentIds(expandedContentIds.filter((item) => item !== id));
    } else {
      setExpandedContentIds([...expandedContentIds, id]);
    }
  };

  const isContentExpanded = (id: string) => {
    return expandedContentIds.includes(id);
  };

  const createOrUpdateDraftPost = (post: DraftPost) => {
    const existingDraft = draftPosts.find((p) => p.draftId === post.draftId);
    if (existingDraft) {
      setDraftPosts(draftPosts.map((p) => (p.draftId === post.draftId ? post : p)));
    } else {
      setDraftPosts([...draftPosts, post]);
    }
    saveKeyInLocalStorage(draftPosts);
    return post;
  };

  const removeDraftPost = (id: string) => {
    setDraftPosts(draftPosts.filter((post) => post.draftId !== id));
    saveKeyInLocalStorage(draftPosts);
  };

  const getDraftPost = (id: string) => {
    return draftPosts.find((post) => post.draftId === id);
  };

  return (
    <GlobalStateContext.Provider
      value={{
        expandedContentIds,
        expandCollapseContent,
        isContentExpanded,
        isPostToolbarVisible,
        setIsPostToolbarVisible,
        draftPosts,
        createOrUpdateDraftPost,
        getDraftPost,
        removeDraftPost,
        lastConversationId,
        setLastConversationId,
      }}
    >
      {children}
    </GlobalStateContext.Provider>
  );
};

export default GlobalStateProvider;
